import React, { useState } from 'react';
import {
    Table, TableHead, TableBody, TableRow, TableCell,
    IconButton, Checkbox, TextField, Typography, Paper, Button,
    Box,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogContentText,
    DialogActions
} from '@mui/material';
import { Edit, Delete, Save } from '@mui/icons-material';
import AddIcon from "@mui/icons-material/Add";
import { theme } from '../../../../../theme';
import { SectionProps } from '../CandidateDetailsConfiguration/CandidateDetailsConfiguration';
import { CustomFieldType } from './DocumentsConfiguration';
import { useParams } from 'react-router-dom';
import { addDocumentCustomField, deleteDocumentCustomField } from '../preboardingConfigurationApi';
import { useSnackbar } from '../../../../../Context/SnackbarContext';



interface DocumentSectionTableProps {
    fields: CustomFieldType[];
    setFields: React.Dispatch<React.SetStateAction<CustomFieldType[]>>;
    sectionData: SectionProps,
    isNew: boolean;
}

const DocumentSectionTable: React.FC<DocumentSectionTableProps> = ({
    fields,
    setFields,
    sectionData,
    isNew
}) => {
    const [customRows, setCustomRows] = useState<{ name: string; mandatory: boolean }[]>([]);
    const [editId, setEditId] = useState<number | null>(null);
    const [editValue, setEditValue] = useState({ name: '', mandatory: false });
    const { tenant } = useParams<{ tenant: string }>();
    const { showSnackbar } = useSnackbar();
    const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
    const [customFieldIdToDelete, setCustomFieldIdToDelete] = useState<number | null>(null);
    const [errors, setErrors] = useState<{ [key: string]: string }>({});

    const cancelDeleteCustomField = () => {
        setCustomFieldIdToDelete(null);
        setDeleteDialogOpen(false);
    }

    const handleAddCustomRow = () => {
        setCustomRows([...customRows, { name: '', mandatory: false }]);
    };

    const handleSaveCustomRow = async (index: number) => {
        const row = customRows[index];

        // Reset error for this field
        setErrors(prev => ({ ...prev, [index]: "" }));

        // Validation
        if (!row.name.trim()) {
            setErrors(prev => ({ ...prev, [index]: "Document name is required" }));
            return;
        }

        // Validation - Length
        if (row.name.trim().length > 300) {
            setErrors(prev => ({ ...prev, [index]: "Document name must be less than 300 characters" }));
            return;
        }
        const payLoad = {
            CustomFieldId: 0,
            FieldName: row.name,
            Mandatory: row.mandatory,
            SectionId: sectionData.sectionId,
        };


        try {
            const response = await addDocumentCustomField(payLoad, tenant)

            if (response) {
                showSnackbar(response.Message, response.Status ? "success" : "error")
                if (response.Status) {
                    setFields(
                        [...fields,
                        {
                            customFieldId: response.CustomFieldId,
                            name: row.name,
                            mandatory: row.mandatory,
                            sectionId: sectionData.sectionId,
                            sectionName: sectionData.sectionName
                        }
                        ])
                    setCustomRows(customRows.filter((_, i) => i !== index));
                }

            }
        } catch (error) {
            console.log(error)
        }
    };

    const handleDeleteCustomRow = (index: number) => {
        setCustomRows(customRows.filter((_, i) => i !== index));
    };

    const handleConfirmDelete = async () => {
        if (!customFieldIdToDelete) {
            return
        }
        const payLoad = {
            CustomFieldId: customFieldIdToDelete
        }
        try {
            const response = await deleteDocumentCustomField(payLoad, tenant)
            if (response) {
                if (response.Status) {
                    setFields(prev => prev.filter(field => field.customFieldId !== customFieldIdToDelete));
                    if (editId === customFieldIdToDelete) {
                        setEditId(null);
                        setEditValue({ name: '', mandatory: false });
                    }
                }
                showSnackbar(response.Message, response.Status ? "success" : "error")
            }
        } catch (error) {
            console.log(error)
            showSnackbar("Failed to delete custom field.Please try again.", "error")
        } finally {
            setDeleteDialogOpen(false)
            setCustomFieldIdToDelete(null)
        }


    };

    const handleEdit = (field: CustomFieldType) => {
        setEditId(field.customFieldId);
        setEditValue({ name: field.name, mandatory: field.mandatory });
    };

    const handleSaveEdit = async () => {
        // Find the field being edited
        setErrors(prev => ({ ...prev, edit: "" }));

        if (!editValue.name.trim()) {
            setErrors(prev => ({ ...prev, edit: "Document name is required" }));
            return;
        }

        // Validation - Length
        if (editValue.name.trim().length > 300) {
            setErrors(prev => ({ ...prev, edit: "Document name must be less than 300 characters" }));
            return;
        }
        const fieldToEdit = fields.find(f => f.customFieldId === editId);
        if (!fieldToEdit) {
            return;
        }

        const payLoad = {
            CustomFieldId: editId,
            FieldName: editValue.name,
            Mandatory: editValue.mandatory,
            SectionId: sectionData.sectionId
        };


        try {
            const response = await addDocumentCustomField(payLoad, tenant); // <-- your API for updating

            if (response) {
                if (response.Status) {
                    setFields(prev =>
                        prev.map(field =>
                            field.customFieldId === editId
                                ? { ...field, name: editValue.name, mandatory: editValue.mandatory }
                                : field
                        )
                    );
                }

                showSnackbar(response.Message, response.Status ? "success" : "error");
            }
        } catch (error) {
            console.error(error);
            showSnackbar("Failed to update the field", "error");
        }

        setEditId(null);
        setEditValue({ name: '', mandatory: false });
    };


    return (
        <Box>
            <Typography variant="caption">
                {sectionData?.sectionName}
            </Typography>
            <Paper elevation={1} sx={{ mt: 1, mb: 3 }}>
                <Table>
                    <TableHead>
                        <TableRow>
                            <TableCell sx={{ textAlign: 'center', width: "40%" }}>
                                <Typography variant='caption'>Document</Typography>
                            </TableCell>
                            <TableCell sx={{ textAlign: 'center', width: "30%" }}>
                                <Typography variant='caption'>Mandatory Field</Typography>
                            </TableCell>
                            <TableCell sx={{ textAlign: 'center', width: "30%" }}>
                                <Typography variant='caption'>Action</Typography>
                            </TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {fields.length === 0 && customRows.length === 0 ? (
                            <TableRow>
                                <TableCell colSpan={3} sx={{ textAlign: 'center', py: 3 }}>
                                    <Typography variant="body2" color="text.secondary">
                                        No records found
                                    </Typography>
                                </TableCell>
                            </TableRow>
                        ) : (
                            <>
                                {fields.map((field,index) => (
                                    <TableRow key={index} sx={{ bgcolor: index % 2 !== 0 ? 'inherit' : theme.palette.primary.light }}>
                                        <TableCell sx={{ textAlign: 'center', width: "40%" }}>
                                            {editId === field.customFieldId ? (
                                                <TextField
                                                    size="small"
                                                    sx={{ width: '20vw' }}
                                                    value={editValue.name}
                                                    onChange={(e) => {
                                                        setEditValue({ ...editValue, name: e.target.value });

                                                        // Clear error when typing
                                                        if (errors.edit) {
                                                            setErrors(prev => ({ ...prev, edit: "" }));
                                                        }
                                                    }}
                                                    error={!!errors.edit}
                                                    helperText={errors.edit}
                                                />
                                            ) : (
                                                <Typography variant="subtitle3">
                                                    {field.name}
                                                    {field.mandatory && (
                                                        <span style={{ color: theme.palette.error.main }}> *</span>
                                                    )}
                                                </Typography>

                                            )}
                                        </TableCell>
                                        <TableCell sx={{ textAlign: 'center', width: "30%" }}>
                                            <Checkbox
                                                checked={editId === field.customFieldId ? editValue.mandatory : field.mandatory}
                                                disabled={editId !== field.customFieldId}
                                                onChange={(e) => {
                                                    if (editId === field.customFieldId) {
                                                        setEditValue({ ...editValue, mandatory: e.target.checked });
                                                    }
                                                }}
                                                sx={{
                                                    color: theme.palette.primary.main,
                                                    '&.Mui-checked': {
                                                        color: theme.palette.primary.main,
                                                    },
                                                    '&.Mui-disabled': {
                                                        color: theme.palette.primary.main,
                                                    },
                                                }}
                                            />
                                        </TableCell>
                                        <TableCell sx={{ textAlign: 'center', width: "30%" }}>
                                            {editId === field.customFieldId ? (
                                                <IconButton color="primary" onClick={handleSaveEdit} title='Save Custom Field'>
                                                    <Save fontSize="small" />
                                                </IconButton>
                                            ) : (
                                                <IconButton color='primary' onClick={() => handleEdit(field)} title='Edit Custom Field'>
                                                    <Edit fontSize="small" />
                                                </IconButton>
                                            )}
                                            <IconButton color='primary' onClick={() => {
                                                setDeleteDialogOpen(true);
                                                setCustomFieldIdToDelete(field.customFieldId);
                                            }}
                                                title='Delete Custom Field'>
                                                <Delete fontSize="small" />
                                            </IconButton>
                                        </TableCell>
                                    </TableRow>
                                ))}

                                {/* Custom Field Row */}
                                {customRows.map((row, index) => (
                                    <TableRow key={index}>
                                        <TableCell sx={{ textAlign: 'center' }}>
                                            <TextField
                                                size="small"
                                                sx={{ width: '20vw' }}
                                                placeholder="Enter document name"
                                                value={row.name}
                                                onChange={(e) => {
                                                    const updated = [...customRows];
                                                    updated[index].name = e.target.value;
                                                    setCustomRows(updated);

                                                    // Clear error when typing
                                                    if (errors[index]) {
                                                        setErrors(prev => ({ ...prev, [index]: "" }));
                                                    }
                                                }}
                                                error={!!errors[index]}
                                                helperText={errors[index]}
                                                autoFocus= {isNew}
                                            />
                                        </TableCell>
                                        <TableCell sx={{ textAlign: 'center' }}>
                                            <Checkbox
                                                checked={row.mandatory}
                                                onChange={(e) => {
                                                    const updated = [...customRows];
                                                    updated[index].mandatory = e.target.checked;
                                                    setCustomRows(updated);
                                                }}
                                            />
                                        </TableCell>
                                        <TableCell sx={{ textAlign: 'center' }}>
                                            <IconButton color="primary" onClick={() => handleSaveCustomRow(index)}>
                                                <Save fontSize="small" />
                                            </IconButton>
                                            <IconButton color='primary' onClick={() => handleDeleteCustomRow(index)}>
                                                <Delete fontSize="small" />
                                            </IconButton>
                                        </TableCell>
                                    </TableRow>
                                ))}
                            </>
                        )}



                    </TableBody>
                </Table>

            </Paper>
            <Box mt={2} display="flex" justifyContent="flex-end">
                <Button
                    variant="outlined"
                    startIcon={<AddIcon />}
                    sx={{
                        color: theme.palette.primary.main,
                        borderColor: theme.palette.primary.main,
                        ml: "auto"
                    }}
                    onClick={handleAddCustomRow}
                >
                    Add Custom Field
                </Button>
            </Box>

            {/* Delete Confirmation Dialog */}
            <Dialog
                open={deleteDialogOpen}
                onClose={cancelDeleteCustomField}
            >
                <DialogTitle>
                    Confirm Delete
                </DialogTitle>
                <DialogContent>
                    <DialogContentText>
                        Are you sure you want to delete this custom field? This action cannot be undone.
                    </DialogContentText>
                </DialogContent>
                <DialogActions>
                    <Button onClick={cancelDeleteCustomField} sx={{ color: theme.palette.text.secondary }}>
                        Cancel
                    </Button>
                    <Button
                        variant="contained"
                        onClick={handleConfirmDelete}
                        sx={{
                            bgcolor: theme.palette.error.main,
                            '&:hover': {
                                bgcolor: theme.palette.error.dark,
                            },
                        }}>
                        Confirm
                    </Button>
                </DialogActions>
            </Dialog>
        </Box>

    );
};

export default DocumentSectionTable;